/**
 * The OpenAI ChatKit web component's CDN script, loaded only by the ChatKit chat panel (Channels C0b P5, C0B-DESIGN
 * 3.1). It used to be a static tag in index.html, so every page of every environment fetched it; now the agents surface
 * (the Solo panel) never asks for it, and the ChatKit surface (test and production until C8) asks for it the first
 * time its panel opens. C8 (AB#20884) deletes this file with the ChatKit panel.
 */
export const CHATKIT_SCRIPT_URL = 'https://cdn.platform.openai.com/deployments/chatkit/chatkit.js';

/** The id of the one script tag this loader adds. */
export const CHATKIT_SCRIPT_ID = 'solo-openai-chatkit-script';

let loading: Promise<void> | null = null;

/**
 * Adds the ChatKit script to the page once and resolves when it has loaded (or the element is already defined). A
 * failed load is forgotten, so the panel's Retry asks again.
 */
export function loadChatKitScript(doc: Document = document): Promise<void> {
  if (doc.defaultView?.customElements?.get('openai-chatkit')) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise<void>((resolve, reject) => {
    const existing = doc.getElementById(CHATKIT_SCRIPT_ID);
    existing?.remove();
    const script = doc.createElement('script');
    script.id = CHATKIT_SCRIPT_ID;
    script.src = CHATKIT_SCRIPT_URL;
    script.async = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => {
      loading = null;
      script.remove();
      reject(new Error('ChatKit script failed to load'));
    }, { once: true });
    doc.head.appendChild(script);
  });
  return loading;
}

/** Test seam: forget a load in progress. */
export function resetChatKitScriptLoaderForTests(): void {
  loading = null;
}
